import { DatePicker, Popover, Select } from "antd";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { FilterPill } from "./ui";

const { RangePicker } = DatePicker;

export interface QuickRange {
    value: string;
    label: string;
    range: [dayjs.Dayjs, dayjs.Dayjs];
    previousRange: [dayjs.Dayjs, dayjs.Dayjs];
}

export const defaultQuickRanges: QuickRange[] = [
    {
        value: 'all-time',
        label: 'All Time',
        range: [dayjs('2000-01-01').startOf('day'), dayjs().endOf('day')],
        previousRange: [dayjs('2000-01-01').startOf('day'), dayjs('2000-01-01').endOf('day')]
    },
    {
        value: 'today',
        label: 'Today',
        range: [dayjs().startOf('day'), dayjs().endOf('day')],
        previousRange: [dayjs().subtract(1, 'day').startOf('day'), dayjs().subtract(1, 'day').endOf('day')]
    },
    {
        value: 'yesterday',
        label: 'Yesterday',
        range: [dayjs().subtract(1, 'day').startOf('day'), dayjs().subtract(1, 'day').endOf('day')],
        previousRange: [dayjs().subtract(2, 'day').startOf('day'), dayjs().subtract(2, 'day').endOf('day')]
    },
    {
        value: 'last-3-days',
        label: 'Last 3 Days',
        range: [dayjs().subtract(2, 'day').startOf('day'), dayjs().endOf('day')],
        previousRange: [dayjs().subtract(5, 'day').startOf('day'), dayjs().subtract(3, 'day').endOf('day')]
    },
    {
        value: 'last-7-days',
        label: 'Last 7 Days',
        range: [dayjs().subtract(6, 'day').startOf('day'), dayjs().endOf('day')],
        previousRange: [dayjs().subtract(13, 'day').startOf('day'), dayjs().subtract(7, 'day').endOf('day')]
    },
    {
        value: 'this-month',
        label: 'This Month',
        range: [dayjs().startOf('month'), dayjs().endOf('month')],
        previousRange: (() => {
            const lastMonth = dayjs().subtract(1, 'month');
            const currentDayOfMonth = dayjs().date();
            const lastMonthSameDay = lastMonth.date(Math.min(currentDayOfMonth, lastMonth.daysInMonth()));
            return [lastMonth.startOf('month'), lastMonthSameDay.endOf('day')] as [dayjs.Dayjs, dayjs.Dayjs];
        })()
    },
    {
        value: 'last-month',
        label: 'Last Month',
        range: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')],
        previousRange: [dayjs().subtract(2, 'month').startOf('month'), dayjs().subtract(2, 'month').endOf('month')]
    },
    {
        value: 'last-3-months',
        label: 'Last 3 Months',
        range: [dayjs().subtract(3, 'month').startOf('month'), dayjs().endOf('month')],
        previousRange: [dayjs().subtract(6, 'month').startOf('month'), dayjs().subtract(3, 'month').endOf('month')]
    },
    {
        value: 'last-6-months',
        label: 'Last 6 Months',
        range: [dayjs().subtract(6, 'month').startOf('month'), dayjs().endOf('month')],
        previousRange: [dayjs().subtract(12, 'month').startOf('month'), dayjs().subtract(6, 'month').endOf('month')]
    },
    {
        value: 'this-year',
        label: 'This Year',
        range: [dayjs().startOf('year'), dayjs().endOf('year')],
        previousRange: [dayjs().subtract(1, 'year').startOf('year'), dayjs().subtract(1, 'year').endOf('year')]
    },
    {
        value: 'last-year',
        label: 'Last Year',
        range: [dayjs().subtract(1, 'year').startOf('year'), dayjs().subtract(1, 'year').endOf('year')],
        previousRange: [dayjs().subtract(2, 'year').startOf('year'), dayjs().subtract(2, 'year').endOf('year')]
    }
];

export interface DateRangeFilterProps {
    dateRange: [dayjs.Dayjs, dayjs.Dayjs] | null;
    onDateRangeChange: (dateRange: [dayjs.Dayjs, dayjs.Dayjs] | null) => void;
    selectedQuickRange: string | null;
    onQuickRangeChange: (value: string | null) => void;
    quickRanges?: QuickRange[];
    showPreviousPeriod?: boolean;
    allowEmpty?: boolean;
}

const DateRangeFilter = (props: DateRangeFilterProps) => {
    const quickRanges = props.quickRanges || defaultQuickRanges;

    function onRangePickerChange(dates: any) {
        if (!dates && !props.allowEmpty) {
            return;
        }
        props.onDateRangeChange(dates);
        props.onQuickRangeChange(null);
    }

    function onQuickRangeChange(value: string, option: any) {
        if (!value || !option) {
            if (props.allowEmpty) {
                props.onDateRangeChange(null);
                props.onQuickRangeChange(null);
            }
            return;
        }
        props.onDateRangeChange(option.range);
        props.onQuickRangeChange(value);
    }

    const previousPeriodRange = useMemo(() => {
        if (!props.dateRange) {
            return null;
        }
        if (props.selectedQuickRange === 'all-time') {
            return null;
        }
        if (props.selectedQuickRange) {
            const selectedRange = quickRanges.find(r => r.value === props.selectedQuickRange);
            if (selectedRange) {
                return selectedRange.previousRange;
            }
        }
        const currentDuration = props.dateRange[1].diff(props.dateRange[0]);
        const previousEnd = props.dateRange[0].add(-1, 'day').endOf('day');
        const previousStart = previousEnd.add(currentDuration * -1);
        return [previousStart, previousEnd] as [dayjs.Dayjs, dayjs.Dayjs];
    }, [props.dateRange, props.selectedQuickRange]);

    return (
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <RangePicker
                value={props.dateRange}
                onChange={onRangePickerChange}
                allowClear={props.allowEmpty}
                placeholder={['Start Date', 'End Date']}
            />
            <Select
                placeholder="Quick Range"
                style={{ width: 200 }}
                value={props.selectedQuickRange}
                onChange={onQuickRangeChange}
                options={quickRanges}
                allowClear={props.allowEmpty}
            />
            {props.showPreviousPeriod && previousPeriodRange && (
                <span style={{ fontSize: '13px', color: '#666' }}>
                    <strong>Previous Period:</strong> {previousPeriodRange[0].format('MMM D, YYYY')} - {previousPeriodRange[1].format('MMM D, YYYY')}
                </span>
            )}
        </div>
    );
};

export function useDateRangeFilter(defaultQuickRange: string): {
    dateRange: [dayjs.Dayjs, dayjs.Dayjs];
    setDateRange: React.Dispatch<React.SetStateAction<[dayjs.Dayjs, dayjs.Dayjs] | null>>;
    selectedQuickRange: string | null;
    setSelectedQuickRange: React.Dispatch<React.SetStateAction<string | null>>;
    previousPeriodRange: [dayjs.Dayjs, dayjs.Dayjs];
};
export function useDateRangeFilter(defaultQuickRange?: null): {
    dateRange: [dayjs.Dayjs, dayjs.Dayjs] | null;
    setDateRange: React.Dispatch<React.SetStateAction<[dayjs.Dayjs, dayjs.Dayjs] | null>>;
    selectedQuickRange: string | null;
    setSelectedQuickRange: React.Dispatch<React.SetStateAction<string | null>>;
    previousPeriodRange: [dayjs.Dayjs, dayjs.Dayjs] | null;
};
export function useDateRangeFilter(defaultQuickRange?: string | null) {
    const quickRanges = defaultQuickRanges;
    const defaultRange = defaultQuickRange ? quickRanges.find(r => r.value === defaultQuickRange) : null;

    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(
        defaultRange?.range || null
    );
    const [selectedQuickRange, setSelectedQuickRange] = useState<string | null>(defaultQuickRange || null);

    const previousPeriodRange = useMemo(() => {
        if (!dateRange) {
            return null;
        }
        if (selectedQuickRange === 'all-time') {
            return null;
        }
        if (selectedQuickRange) {
            const selectedRange = quickRanges.find(r => r.value === selectedQuickRange);
            if (selectedRange) {
                return selectedRange.previousRange;
            }
        }
        const currentDuration = dateRange[1].diff(dateRange[0]);
        const previousEnd = dateRange[0].add(-1, 'day').endOf('day');
        const previousStart = previousEnd.add(currentDuration * -1);
        return [previousStart, previousEnd] as [dayjs.Dayjs, dayjs.Dayjs];
    }, [dateRange, selectedQuickRange]);

    return {
        dateRange,
        setDateRange,
        selectedQuickRange,
        setSelectedQuickRange,
        previousPeriodRange,
    };
}

export interface DateRangePopoverProps {
    dateRange: [dayjs.Dayjs, dayjs.Dayjs] | null;
    setDateRange: React.Dispatch<React.SetStateAction<[dayjs.Dayjs, dayjs.Dayjs] | null>>;
    selectedQuickRange: string | null;
    setSelectedQuickRange: React.Dispatch<React.SetStateAction<string | null>>;
    loading?: boolean;
}

export function DateRangePopover({
    dateRange,
    setDateRange,
    selectedQuickRange,
    setSelectedQuickRange,
    loading,
}: DateRangePopoverProps) {
    const [open, setOpen] = useState(false);
    const label = selectedQuickRange
        ? defaultQuickRanges.find((r) => r.value === selectedQuickRange)?.label ?? selectedQuickRange
        : dateRange
        ? `${dateRange[0].format('MMM D')} – ${dateRange[1].format('MMM D')}`
        : '—';

    function applyCustomRange(dates: any) {
        if (!dates || !dates[0] || !dates[1]) return;
        setDateRange([dates[0], dates[1]]);
        setSelectedQuickRange(null);
    }

    return (
        <Popover
            open={open}
            onOpenChange={setOpen}
            trigger="click"
            placement="bottomRight"
            content={
                <div className="flex w-[440px]">
                    {/* Quick ranges */}
                    <div className="flex flex-col w-[160px] py-1 border-r border-border">
                        <div className="px-3 pt-1 pb-1.5 text-[10px] uppercase tracking-wider text-text-3 font-semibold">
                            Quick range
                        </div>
                        {defaultQuickRanges.map((r) => {
                            const active = selectedQuickRange === r.value;
                            return (
                                <button
                                    key={r.value}
                                    type="button"
                                    onClick={() => {
                                        setDateRange(r.range);
                                        setSelectedQuickRange(r.value);
                                        setOpen(false);
                                    }}
                                    className={`text-left mx-1 px-2 py-1.5 text-[12.5px] rounded hover:bg-bg-elevated ${
                                        active ? 'text-accent font-semibold bg-accent-soft' : 'text-text-2'
                                    }`}
                                >
                                    {r.label}
                                </button>
                            );
                        })}
                    </div>

                    {/* Custom range */}
                    <div className="flex-1 py-1 px-3 flex flex-col gap-2">
                        <div className="pt-1 pb-0.5 text-[10px] uppercase tracking-wider text-text-3 font-semibold">
                            Custom range
                        </div>
                        <DatePicker.RangePicker
                            value={dateRange}
                            onChange={applyCustomRange}
                            allowClear={false}
                            size="small"
                            className="w-full"
                        />
                        {dateRange && (
                            <div className="text-[11px] text-text-3 font-mono leading-snug mt-1">
                                <div>start <span className="text-text-2">{dateRange[0].format('MMM D, YYYY')}</span></div>
                                <div>end <span className="text-text-2">{dateRange[1].format('MMM D, YYYY')}</span></div>
                                <div>days <span className="text-text-2">{dateRange[1].diff(dateRange[0], 'day') + 1}</span></div>
                            </div>
                        )}
                    </div>
                </div>
            }
        >
            <span className="inline-flex items-center gap-2">
                {loading && (
                    <span className="text-[10.5px] font-mono text-text-3">loading…</span>
                )}
                <FilterPill label="Date" value={label} active={!!selectedQuickRange || !!dateRange} />
            </span>
        </Popover>
    );
}

export default DateRangeFilter;
